import React, { useState } from "react";
import confetti from "canvas-confetti";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import api from "@/lib/api";

export function MemberApplyDialog({ open, onClose }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", date_of_birth: "",
    gender: "male", membership_type: "Player",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.date_of_birth) {
      const { toast } = await import("sonner");
      toast.error("Please fill out all fields");
      return;
    }
    setBusy(true);
    try {
      await api.post("/applications/member", form);
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
      const { toast } = await import("sonner");
      toast.success("Application submitted! An admin will share your Member ID once approved.");
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="p-0 w-[95vw] max-w-2xl max-h-[90dvh] overflow-y-auto scrollbar-hide rounded-xl border-0 shadow-2xl bg-white text-foreground [&>button]:hidden"
        data-testid="member-apply-dialog"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Become a member</DialogTitle>
        </DialogHeader>
        <div className="p-6 sm:p-8">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Step 01 · Join the club</div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1">
            Become a <span className="font-serif italic text-[hsl(var(--accent))]">member</span>.
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            A short form. An admin reviews your application and shares a unique Member ID with you once approved.
          </p>

          <form className="space-y-5" onSubmit={submit} onClick={(e) => e.stopPropagation()}>
            <Card className="p-5 rounded-2xl space-y-5">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Personal details</div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="dlg-first_name" className="text-sm font-medium">First name</Label>
                  <Input id="dlg-first_name" data-testid="apply-dlg-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5 h-11 rounded-lg" required />
                </div>
                <div>
                  <Label htmlFor="dlg-last_name" className="text-sm font-medium">Last name</Label>
                  <Input id="dlg-last_name" data-testid="apply-dlg-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1.5 h-11 rounded-lg" required />
                </div>
              </div>
              <div>
                <Label htmlFor="dlg-email" className="text-sm font-medium">Email</Label>
                <Input id="dlg-email" type="email" data-testid="apply-dlg-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11 rounded-lg" required />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="dlg-phone" className="text-sm font-medium">Phone</Label>
                  <Input id="dlg-phone" type="tel" data-testid="apply-dlg-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 h-11 rounded-lg" required />
                </div>
                <div>
                  <Label htmlFor="dlg-dob" className="text-sm font-medium">Date of birth</Label>
                  <Input id="dlg-dob" type="date" data-testid="apply-dlg-dob" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="mt-1.5 h-11 rounded-lg" required />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Gender</Label>
                <RadioGroup value={form.gender} onValueChange={(v) => set("gender", v)} className="flex gap-4">
                  {["male", "female", "other"].map((g) => (
                    <div key={g} className="flex items-center gap-2">
                      <RadioGroupItem value={g} id={`dlg-gender-${g}`} />
                      <Label htmlFor={`dlg-gender-${g}`} className="text-sm capitalize">{g}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </Card>
            <Card className="p-5 rounded-2xl space-y-4">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Membership type</div>
              <RadioGroup value={form.membership_type} onValueChange={(v) => set("membership_type", v)} className="grid sm:grid-cols-3 gap-3">
                {["Player", "Social", "Junior"].map((t) => (
                  <label key={t} className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${form.membership_type === t ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5" : "border-border hover:border-muted-foreground/30"}`}>
                    <RadioGroupItem value={t} id={`dlg-type-${t}`} />
                    <span className="text-sm font-medium">{t}</span>
                  </label>
                ))}
              </RadioGroup>
            </Card>
            <Button
              type="submit"
              disabled={busy}
              className="w-full rounded-full h-12 text-base font-semibold bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90"
              data-testid="apply-dlg-submit"
            >
              {busy ? "Submitting…" : "Submit application"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
